/**
 * Theme bootstrap — synchronous helpers that run BEFORE Vue/Pinia mount, to
 * apply the persisted theme to `<html>` ahead of the first paint (anti-FOUC).
 *
 * Single source of truth for:
 *   - Storage key + matchMedia query string
 *   - Persisted-mode hydration
 *   - OS preference snapshot
 *   - Document class application
 *
 * The Pinia `theme` store imports these helpers (no duplication). The store
 * additionally owns the runtime matchMedia *listener* and reactive state —
 * this module only does the one-shot "before mount" application.
 */

export const STORAGE_KEY = 'eoscope-theme-mode'
export const DARK_QUERY = '(prefers-color-scheme: dark)'

export type ThemeMode = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

export function isThemeMode(v: unknown): v is ThemeMode {
  return v === 'light' || v === 'dark' || v === 'system'
}

/** Read the persisted mode from localStorage. Defensive — storage may be
 *  unavailable (private mode), missing, or carry a stale value from an older
 *  schema. Any failure falls back to `'system'`. */
export function hydrateMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return isThemeMode(raw) ? raw : 'system'
  } catch {
    return 'system'
  }
}

/** Snapshot the OS preference. `matchMedia` is guarded for SSR/test
 *  environments that don't expose `window.matchMedia`. */
export function readSystemPreference(): EffectiveTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light'
  }
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

/** Toggle the `.dark` class on `<html>`. SSR-safe. */
export function applyThemeClass(theme: EffectiveTheme): void {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', theme === 'dark')
}

/** Resolve a `ThemeMode` to an `EffectiveTheme` given the current system pref. */
export function resolveEffective(
  mode: ThemeMode,
  systemPref: EffectiveTheme,
): EffectiveTheme {
  return mode === 'system' ? systemPref : mode
}

/**
 * Apply the persisted theme to `<html>` synchronously, BEFORE Vue mounts.
 * Eliminates the light→dark flash on reload. Safe to call multiple times
 * (idempotent — just toggles the class).
 */
export function bootstrapTheme(): void {
  const mode = hydrateMode()
  const systemPref = readSystemPreference()
  const effective = resolveEffective(mode, systemPref)
  applyThemeClass(effective)
}
